import { Alert, Button, Typography } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { CreateBulkExportRequest } from "../../../../api/bulkExport";
import DialogForm from "../../../../components/common/DialogForm/DialogForm";
import { useAlert } from "../../../../context/AlertContext";
import { useCreateBulkExport } from "../../../../hooks/useBulkExport";
import Interaction from "../../../../types/interaction/Interaction";
import {
  BulkExportData,
  BulkExportFormValues,
} from "../../../../types/interaction/BulkExport";
import { BulkExportDataTypeDropdown } from "./BulkExportDataTypeDropdown";
import LoadingOverlay from "../../../../components/common/LoadingIndicator/LoadingOverlay";

export interface BulkExportModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  customerId: string | undefined;
  interactions: Interaction[];
}

const FORM_ID = "bulk-export";

export function BulkExportModal({
  isOpen,
  setIsOpen,
  customerId,
  interactions,
}: Readonly<BulkExportModalProps>) {
  const showAlert = useAlert();
  const navigateTo = useNavigate();
  const {
    mutateAsync,
    reset: resetMutation,
    isPending,
    isSuccess,
  } = useCreateBulkExport();

  const defaultValues = useCallback(() => {
    return {
      customerId,
      interactionIds: interactions.map(
        (interaction) => interaction.interactionId
      ),
      dataToExport: [
        { value: BulkExportData.Metadata, label: "Metadata" },
        { value: BulkExportData.Media, label: "Media" },
        { value: BulkExportData.Transcript, label: "Transcript" },
      ],
    };
  }, [customerId, interactions]);

  const { handleSubmit, reset, control, formState } =
    useForm<BulkExportFormValues>({
      defaultValues: defaultValues(),
    });
  const { isSubmitting, isValid } = formState;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    reset(defaultValues());

    return () => {
      resetMutation();
    };
  }, [defaultValues, isOpen, reset, resetMutation]);

  function transform(
    formValues: BulkExportFormValues
  ): CreateBulkExportRequest {
    return {
      customerId: formValues.customerId,
      interactionIds: formValues.interactionIds,
      dataToExport: formValues.dataToExport.map((data) => data.value),
    };
  }

  async function startBulkExport(formValues: BulkExportFormValues) {
    try {
      const request = transform(formValues);
      await mutateAsync(request);
      showAlert(
        "Bulk export request started successfully. You can check the status on the 'Bulk Export' page.",
        "success"
      );
    } catch (error) {
      console.error("Error starting bulk export:", error);
      showAlert("Failed to start bulk export request.", "error");
    }
  }

  return (
    <DialogForm
      formId={FORM_ID}
      dialogTitle="Bulk Export"
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      isSubmitting={isSubmitting}
      isCancelDisabled={isSubmitting}
      isSubmitDisabled={!isValid}
      afterClose={reset}
    >
      <form id={FORM_ID} onSubmit={handleSubmit(startBulkExport)} noValidate>
        <LoadingOverlay isLoading={isPending} />
        <Typography variant="body2" className="pb-4">
          Exporting {interactions.length} selected interactions
        </Typography>
        <BulkExportDataTypeDropdown control={control} />
        <Alert severity="info" className="mb-2">
          Bulk export may take a while to process.
        </Alert>
        {isSuccess && (
          <Button
            onClick={() =>
              navigateTo(`/customers/${customerId}/jobs/bulk-export`)
            }
            variant="outlined"
          >
            View Bulk Export Jobs
          </Button>
        )}
      </form>
    </DialogForm>
  );
}
